import { editAdminSelectStyles } from 'components/form/EditSelectStyles';
import LargeRadiusTextInput from 'components/form/LargeRadiusTextInput';
import SortedTable from 'components/form/SortedTable';
import { Formik } from 'formik';
import { isEmpty } from 'lodash';
import { FixedLabel } from 'pages/pcrWizard/PcrWizard';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { EpdLinks } from 'routes/EpdRoutes';
import { CompanyListModel, EPDListModel, EPDStatusDisplayName } from 'services/EpdClient';
import AdminEpdService from 'services/admin/AdminEpdService';
import AdminService from 'services/admin/AdminService';
import { useTheme } from 'styled-components';
import {
  AdminBox,
  AdminSearchGridForm,
  ButtonSmall,
  Container,
  EditFull,
  ErrorMessage,
  H2,
  InfoText,
  LabelBox,
  Message,
  MessageBox,
  ResultMessageBox,
  SearchBox,
  StyledReactSelect,
  ValueBox,
} from 'styles/Styles.styled';
import Spinner from 'styles/spinner-left.styled';
import { Theme } from 'styles/theme';
import { formatDate, PoorMansError } from 'util/utils';
import * as Yup from 'yup';

import { Option } from '../../../types/types';
import { useTranslation } from 'react-i18next';
import { EPDStatusDisplayNameDataSource } from 'components/v2/compiler/constants';

const AdminEpds: React.FunctionComponent = () => {
  const history = useHistory();
  const [companies, setCompanies] = useState<CompanyListModel[]>([]);
  const [epds, setEpds] = useState<EPDListModel[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [epdsNumber, setEpdsNumber] = useState<number | undefined>();
  const theme = useTheme() as Theme;
  const { t } = useTranslation();

  const titles = [
    { label: 'Reg. No.', field: 'fullIdentificationNumber' },
    { label: 'Title', field: 'title' },
    { label: 'EPD Owner', field: 'epdOwnerCompanyName' },
    {
      label: 'Status',
      field: 'status',
      sortComparator: (a: any, b: any, direction: any) => {
        const textA = a.props.children;
        const textB = b.props.children;
        return textA.localeCompare(textB) * direction;
      },
    },
    { label: 'Reg. date', field: 'registrationDate' },
  ];

  const initialValues = {
    registrationNumber: null,
    title: '',
    searchedCompanyName: '',
    selectedCompany: null,
    epdStatusDisplayName: null,
  };

  const validationSchema = Yup.object({
    registrationNumber: Yup.number().nullable(),
    title: Yup.string().nullable(),
    searchedCompanyName: Yup.string().nullable(),
    selectedCompany: Yup.object().nullable(),
    epdStatusDisplayName: Yup.object().nullable(),
  });

  const onSearch = async (rowValues: any) => {
    let values = {
      ...rowValues,
    };

    try {
      setIsLoading(true);
      let allEpds = await AdminEpdService.findEpds(
        values?.selectedCompany?.value,
        values?.selectedCompany?.label ?? values?.searchedCompanyName ?? '',
        values?.title,
        values?.registrationNumber,
        values?.epdStatusDisplayName?.value
      );
      allEpds = allEpds.map((epd) => {
        const statusElementStyle: React.CSSProperties = (() => {
          switch (epd.statusDisplayName) {
            case EPDStatusDisplayName.Published:
              return {
                margin: '0 10px 0 0',
                padding: '5px 2px',
                color: 'white',
                backgroundColor: `${theme.colors.regionColorLight}`,
                borderRadius: '3px',
                textAlign: 'center',
              };
            case EPDStatusDisplayName.Deregistered:
              return {
                margin: '0 10px 0 0',
                padding: '5px 2px',
                color: 'white',
                backgroundColor: `${theme.colors.lightOrange}`,
                borderRadius: '3px',
                textAlign: 'center',
              };
            default:
              return {
                margin: '0 10px 0 0',
                padding: '5px 2px',
                color: 'white',
                backgroundColor: `${theme.colors.darkGray}`,
                borderRadius: '3px',
                textAlign: 'center',
              };
          }
        })();
        const statusElement = (
          <div style={statusElementStyle}>{t(`epdWizard.epdState.status.${epd.statusDisplayName}`)}</div>
        );

        var registrationDate = formatDate(epd.registrationDate) ?? '';

        return { ...epd, status: statusElement, registrationDate };
      });
      setEpds(allEpds);
      setEpdsNumber(allEpds.length);
    } catch (error) {
      setErrorMessage(PoorMansError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const openEpd = (item: any) => {
    history.push(EpdLinks.editEpd(item.id));
  };

  useEffect(() => {
    const loadAccounts = async () => {
      const companyList = await AdminService.getCompanies();
      setCompanies(companyList);
    };
    loadAccounts();
  }, []);

  if (!initialValues) {
    return (
      <div style={{ marginTop: '1rem' }}>
        <Spinner />
      </div>
    );
  }
  return (
    <Container>
      <EditFull>
        <H2>EPDs</H2>
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSearch}>
          <AdminBox>
            <AdminSearchGridForm>
              <LabelBox>
                <FixedLabel>Registration number</FixedLabel>
              </LabelBox>
              <ValueBox>
                <LargeRadiusTextInput
                  style={{ boxSizing: 'border-box', width: '100%' }}
                  name="registrationNumber"
                  type="number"
                  placeholder="Search by Registration Number..."
                />
              </ValueBox>
              <LabelBox>
                <FixedLabel>Title</FixedLabel>
              </LabelBox>
              <ValueBox>
                <LargeRadiusTextInput
                  style={{ boxSizing: 'border-box', width: '100%' }}
                  name="title"
                  type="text"
                  placeholder="Search by EPD title..."
                />
              </ValueBox>
              <LabelBox>
                <FixedLabel>Organisation Name</FixedLabel>
              </LabelBox>
              <ValueBox>
                <LargeRadiusTextInput
                  style={{ boxSizing: 'border-box', width: '100%' }}
                  name="searchedCompanyName"
                  type="text"
                  placeholder="Search by Organisation name..."
                />
              </ValueBox>
              <LabelBox>
                <FixedLabel>Organisation</FixedLabel>
              </LabelBox>
              <ValueBox>
                <StyledReactSelect
                  isMulti={false}
                  name="selectedCompany"
                  styles={editAdminSelectStyles}
                  isClearable={true}
                  defaultValue="Select..."
                  options={companies.map((company) => {
                    return { value: company.id, label: company.name } as Option;
                  })}
                ></StyledReactSelect>
              </ValueBox>
              <LabelBox>
                <FixedLabel>Status</FixedLabel>
              </LabelBox>
              <ValueBox>
                <StyledReactSelect
                  isMulti={false}
                  name="epdStatusDisplayName"
                  styles={editAdminSelectStyles}
                  isClearable={true}
                  defaultValue="Select..."
                  options={EPDStatusDisplayNameDataSource}
                ></StyledReactSelect>
              </ValueBox>
              <SearchBox>
                <ButtonSmall style={{ padding: '0.5rem 1rem', borderRadius: '3px' }} type="submit">
                  Search
                </ButtonSmall>
              </SearchBox>
            </AdminSearchGridForm>
          </AdminBox>
        </Formik>
        {isLoading && (
          <div style={{ marginTop: '1rem' }}>
            <Spinner />
          </div>
        )}
        {!isEmpty(epds) && !isLoading && (
          <MessageBox>
            <ResultMessageBox>
              {epdsNumber === 1 ? (
                <Message style={{ marginLeft: 0 }}>Found {epdsNumber} match</Message>
              ) : (
                <Message>Found {epdsNumber} matches</Message>
              )}
            </ResultMessageBox>
            <div
              style={{
                display: 'inline-block',
                gridColumnStart: '2',
                gridColumnEnd: '3',
                minWidth: '100%',
                padding: '1px',
                borderBottomLeftRadius: '3px',
                borderBottom: `1px solid ${theme.colors.mediumGrey}`,
                alignSelf: 'end',
                justifySelf: 'end',
              }}
            ></div>
          </MessageBox>
        )}
        {errorMessage && isEmpty(epds) && (
          <ErrorMessage style={{ marginTop: '1rem', color: 'black' }}> {errorMessage}</ErrorMessage>
        )}
        {isEmpty(epds) && epds !== null && !isLoading && (
          <InfoText>No results found.</InfoText>
        )}
        {!isEmpty(epds) && !isLoading && (
          <div style={{ marginTop: '-1.2rem' }}>
            <SortedTable columns={titles} collection={epds ?? []} onClick={openEpd} />
          </div>
        )}
      </EditFull>
    </Container>
  );
};

export default AdminEpds;
